import * as THREE from 'three';
import type { BlastEvent } from '../physics/world';

type Point = { x: number; y: number; z: number };
export interface ThermalTarget {
  burnSeconds?: number;
  id: string;
  category: 'building' | 'vegetation' | 'wood' | 'vehicle';
  position: () => Point;
  /** Fixed anchors opt out of the moving index until explicitly invalidated.
   * Omit for independent moving targets such as vehicles and aircraft. */
  positionFixed?: () => boolean;
  /** Available combustible fraction, from zero (inert) to one. */
  fuel: () => number;
  onHeat: (heat: number, dt: number) => void;
  onExplosion?: () => void;
}
export interface FireIncident {
  id: string;
  point: Point;
  heat: number;
  intensity: number;
}
interface ThermalState {
  target: ThermalTarget;
  point: THREE.Vector3;
  bucket: string;
  heat: number;
  intensity: number;
  remaining: number;
  consumed: number;
  wetness: number;
  burning: boolean;
  severeTime: number;
  exploded: boolean;
  phase: number;
}
interface SeenBlast {
  targets: Set<string>;
  time: number;
}

const STEP = 0.1;
const GRID = 20;
const VISUALS = 64;
const MAX_SEEN_BLASTS = 96;
const IGNITION = { building: 0.64, vegetation: 0.47, wood: 0.53, vehicle: 0.86 } as const;
const BURN_SECONDS = { building: 70, vegetation: 45, wood: 65, vehicle: 28 } as const;
const fraction = (value: number): number => (Number.isFinite(value) ? THREE.MathUtils.clamp(value, 0, 1) : 0);
const bucketAt = (p: Point): string => `${Math.floor(p.x / GRID)},${Math.floor(p.z / GRID)}`;

/**
 * Calibrated game heat, not combustion chemistry. Ten-Hz local heat/fuel updates
 * and neighbor transfers run independently of render density. Inert targets still
 * receive heat callbacks; only their available combustible fraction can burn.
 */
export class FireSystem {
  effectDetail = 2;
  reduced = false;
  readonly flames: THREE.InstancedMesh<THREE.ConeGeometry, THREE.MeshStandardMaterial>;
  readonly smoke: THREE.InstancedMesh<THREE.IcosahedronGeometry, THREE.MeshBasicMaterial>;
  private states = new Map<string, ThermalState>();
  private warm = new Set<string>();
  private grid = new Map<string, Set<string>>();
  private moving = new Set<string>();
  private dirtyPositions = new Set<string>();
  private positionRefreshes = 0;
  private fullRefreshes = 0;
  private seen = new Map<string, SeenBlast>();
  private visualStates: ThermalState[] = [];
  private secondaryQueue: { id: string; callback: () => void }[] = [];
  private dummy = new THREE.Object3D();
  private color = new THREE.Color();
  private time = 0;
  private accumulator = 0;
  private indexAge = 0;
  private visualAge = 1;
  private ignitions = 0;
  private secondaryExplosions = 0;
  private transfers = 0;
  private totalConsumed = 0;

  constructor(scene: THREE.Scene) {
    this.flames = new THREE.InstancedMesh(
      new THREE.ConeGeometry(0.5, 1, 5, 1),
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xff731f,
        emissiveIntensity: 1.35,
        roughness: 1,
        transparent: true,
        opacity: 0.83,
        depthWrite: false,
      }),
      VISUALS * 3,
    );
    this.smoke = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(0.65, 1),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.23, depthWrite: false }),
      VISUALS * 3,
    );
    this.flames.name = 'pooled fire';
    this.smoke.name = 'pooled smoke';
    for (const mesh of [this.flames, this.smoke]) {
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      scene.add(mesh);
    }
  }

  register(target: ThermalTarget): void {
    const current = this.states.get(target.id);
    if (current) {
      // Re-registering a streamed representation must not extinguish or refuel it.
      current.target = target;
      current.remaining = Math.min(
        current.remaining,
        Math.max(0, fraction(target.fuel()) - current.consumed),
      );
      this.refreshPosition(current);
      return;
    }
    const point = target.position();
    let hash = 0;
    for (const letter of target.id) hash = (Math.imul(hash, 31) + letter.charCodeAt(0)) | 0;
    const state: ThermalState = {
      target,
      point: new THREE.Vector3(point.x, point.y, point.z),
      bucket: bucketAt(point),
      heat: 0,
      intensity: 0,
      remaining: fraction(target.fuel()),
      consumed: 0,
      wetness: 0,
      burning: false,
      severeTime: 0,
      exploded: false,
      phase: (Math.abs(hash) % 1000) * 0.013,
    };
    this.states.set(target.id, state);
    this.addToBucket(state);
    if (!target.positionFixed?.()) this.moving.add(target.id);
  }

  unregister(id: string): void {
    const state = this.states.get(id);
    if (!state) return;
    const bucket = this.grid.get(state.bucket);
    bucket?.delete(id);
    if (!bucket?.size) this.grid.delete(state.bucket);
    this.states.delete(id);
    this.moving.delete(id);
    this.dirtyPositions.delete(id);
    this.warm.delete(id);
    this.visualAge = 1;
  }

  private addToBucket(state: ThermalState): void {
    let bucket = this.grid.get(state.bucket);
    if (!bucket) this.grid.set(state.bucket, (bucket = new Set()));
    bucket.add(state.target.id);
  }

  private refreshPosition(state: ThermalState): void {
    this.positionRefreshes++;
    const point = state.target.position();
    state.point.set(point.x, point.y, point.z);
    if (state.target.positionFixed?.()) this.moving.delete(state.target.id);
    else this.moving.add(state.target.id);
    const next = bucketAt(point);
    if (next === state.bucket) return;
    const old = this.grid.get(state.bucket);
    old?.delete(state.target.id);
    if (!old?.size) this.grid.delete(state.bucket);
    state.bucket = next;
    this.addToBucket(state);
  }

  private refreshIndex(all = false): void {
    if (all) this.fullRefreshes++;
    for (const id of this.dirtyPositions) this.moving.add(id);
    this.dirtyPositions.clear();
    for (const id of all ? this.states.keys() : this.moving) {
      const state = this.states.get(id);
      if (state) this.refreshPosition(state);
      else this.moving.delete(id);
    }
    this.indexAge = 0;
  }
  /** Coalesce ownership/support changes until the completed physics transaction
   * can provide the original anchor's new position. No partial-owner reads. */
  invalidatePosition(id: string): void {
    if (this.states.has(id)) this.dirtyPositions.add(id);
  }
  /** An explicit shared-event boundary also observes externally moved bodies.
   * Ordinary 4Hz maintenance visits only the dirty/moving work lists. */
  refreshPositions(): void {
    this.refreshIndex(true);
  }

  private nearby(point: Point, radius: number, visit: (state: ThermalState, distance: number) => void): void {
    if (this.dirtyPositions.size) this.refreshIndex();
    // The index refreshes at 4 Hz. Padding includes the travel of fast traffic;
    // exact current positions decide influence, never the old bucket position.
    const padding = 12;
    const minX = Math.floor((point.x - radius - padding) / GRID);
    const maxX = Math.floor((point.x + radius + padding) / GRID);
    const minZ = Math.floor((point.z - radius - padding) / GRID);
    const maxZ = Math.floor((point.z + radius + padding) / GRID);
    for (let x = minX; x <= maxX; x++)
      for (let z = minZ; z <= maxZ; z++) {
        const bucket = this.grid.get(`${x},${z}`);
        if (!bucket) continue;
        for (const id of bucket) {
          const state = this.states.get(id)!;
          const current = this.moving.has(id) ? state.target.position() : state.point;
          state.point.set(current.x, current.y, current.z);
          const distance = Math.hypot(point.x - current.x, point.y - current.y, point.z - current.z);
          if (distance <= radius) visit(state, distance);
        }
      }
  }

  heat(event: BlastEvent): void {
    if (!(event.radius > 0) || !(event.heat > 0)) return;
    if (this.indexAge >= 0.2 || this.dirtyPositions.size) this.refreshIndex();
    const key = `${event.epoch}:${event.id}`;
    let seen = this.seen.get(key);
    if (!seen) {
      while (this.seen.size >= MAX_SEEN_BLASTS) this.seen.delete(this.seen.keys().next().value!);
      this.seen.set(key, (seen = { targets: new Set(), time: this.time }));
    }
    seen.time = this.time;
    this.nearby(event.point, event.radius, (state, distance) => {
      if (seen!.targets.has(state.target.id)) return;
      seen!.targets.add(state.target.id);
      const attenuation = 0.3 + 0.7 * (1 - distance / event.radius);
      this.addHeat(state, event.heat * (event.multiplicity ?? 1) * attenuation);
    });
  }

  private addHeat(state: ThermalState, amount: number): void {
    if (amount <= 0) return;
    state.heat = THREE.MathUtils.clamp(state.heat + amount * (1 - state.wetness * 0.72), 0, 1.6);
    this.warm.add(state.target.id);
    this.visualAge = Math.max(0.15, this.visualAge);
  }
  /** Local ignition from a high-energy impact on a known finite-fuel entity. */
  ignite(id: string): boolean {
    const state = this.states.get(id);
    if (!state || state.target.fuel() <= 0 || state.remaining <= 0) return false;
    this.addHeat(state, 1.5);
    return true;
  }

  cool(id: string, amount: number): void {
    const state = this.states.get(id);
    if (!state || !(amount > 0)) return;
    state.heat = Math.max(0, state.heat - amount);
    state.wetness = Math.min(1, state.wetness + amount * 0.9);
    state.intensity = Math.min(state.intensity, Math.max(0, (state.heat - 0.3) * 1.6));
    if (state.heat < IGNITION[state.target.category] * 0.8) state.burning = false;
    state.severeTime = Math.max(0, state.severeTime - amount * 5);
    this.visualAge = 1;
  }

  update(dt: number): void {
    const elapsed = THREE.MathUtils.clamp(dt, 0, 0.25);
    this.time += elapsed;
    this.indexAge += elapsed;
    this.visualAge += elapsed;
    this.accumulator += elapsed;
    if (this.indexAge >= 0.25) this.refreshIndex();
    let steps = 0;
    while (this.accumulator >= STEP && steps++ < 3) {
      this.accumulator -= STEP;
      this.thermalStep(STEP);
    }
    if (this.visualAge >= 0.15) this.chooseVisuals();
    this.draw();
  }

  private thermalStep(dt: number): void {
    const burning: ThermalState[] = [];
    for (const id of [...this.warm]) {
      const state = this.states.get(id);
      if (!state) {
        this.warm.delete(id);
        continue;
      }
      const category = state.target.category;
      state.remaining = Math.min(
        state.remaining,
        Math.max(0, fraction(state.target.fuel()) - state.consumed),
      );
      state.wetness = Math.max(0, state.wetness - dt * 0.045);
      if (
        !state.burning &&
        state.remaining > 0.005 &&
        state.heat >= IGNITION[category] + state.wetness * 0.3
      ) {
        state.burning = true;
        this.ignitions++;
      }
      if (state.remaining <= 0.002 || state.heat < 0.31) state.burning = false;
      const desired = state.burning
        ? Math.min(1, (state.heat - 0.24) * 1.4) *
          Math.min(1, state.remaining * 10) *
          (1 - state.wetness * 0.8)
        : 0;
      state.intensity += (desired - state.intensity) * Math.min(1, dt * 4);
      if (state.burning) {
        const used = Math.min(
          state.remaining,
          (dt * state.intensity) / (state.target.burnSeconds ?? BURN_SECONDS[category]),
        );
        state.remaining -= used;
        state.consumed += used;
        this.totalConsumed += used;
        state.heat = Math.min(1.6, state.heat + dt * state.intensity * 0.16);
        burning.push(state);
      }
      state.heat = Math.max(
        0,
        state.heat - dt * (0.038 + state.wetness * 0.22 + (state.remaining <= 0.005 ? 0.1 : 0)),
      );
      state.target.onHeat(state.heat, dt);
      if (category === 'vehicle' && !state.exploded && state.target.onExplosion) {
        state.severeTime =
          state.heat >= 0.95 && state.intensity > 0.45 && state.remaining > 0.05
            ? state.severeTime + dt
            : Math.max(0, state.severeTime - dt * 2);
        if (state.severeTime >= 4) {
          // Mark first: the callback may issue another blast into this same system.
          state.exploded = true;
          this.secondaryQueue.push({ id, callback: state.target.onExplosion });
        }
      }
      if (state.heat <= 0.002 && state.intensity <= 0.004) {
        state.heat = 0;
        state.intensity = 0;
        state.target.onHeat(0, dt);
        this.warm.delete(id);
      }
    }
    for (const source of burning)
      this.nearby(source.point, 8.5, (target, distance) => {
        if (source === target) return;
        this.transfers++;
        const transfer = dt * source.intensity * 0.27 * (1 - distance / 8.5) ** 2;
        this.addHeat(target, transfer);
      });
    // A bounded callback drain prevents a traffic pileup from recursively exploding
    // all vehicles in one synchronous call. The remaining callbacks retain identity.
    for (let i = 0; i < 8 && this.secondaryQueue.length; i++) {
      const queued = this.secondaryQueue.shift()!;
      if (!this.states.has(queued.id)) continue;
      this.secondaryExplosions++;
      queued.callback();
    }
    for (const [id, entry] of this.seen) if (this.time - entry.time > 30) this.seen.delete(id);
  }

  private chooseVisuals(): void {
    this.visualAge = 0;
    const limit = this.reduced ? 16 : this.effectDetail <= 0 ? 24 : this.effectDetail === 1 ? 40 : VISUALS;
    this.visualStates = [...this.warm]
      .map((id) => this.states.get(id)!)
      .filter(
        (state) =>
          state &&
          (state.intensity > 0.025 ||
            (state.target.category === 'vehicle' && state.heat > 0.55 && state.remaining > 0)),
      )
      .sort((a, b) => b.intensity - a.intensity || b.heat - a.heat || a.target.id.localeCompare(b.target.id))
      .slice(0, limit);
  }

  private draw(): void {
    let flames = 0;
    let smoke = 0;
    const count = this.reduced || this.effectDetail <= 0 ? 1 : this.effectDetail === 1 ? 2 : 3;
    this.smoke.material.opacity = this.reduced ? 0.13 : 0.23;
    for (const state of this.visualStates) {
      if (!this.states.has(state.target.id)) continue;
      const p = state.target.position();
      const strength = state.intensity;
      const footprint =
        state.target.category === 'building' ? 2.2 : state.target.category === 'vehicle' ? 1.25 : 1;
      for (let part = 0; part < count; part++) {
        const phase = state.phase + part * 2.1;
        const flicker = this.reduced
          ? 0.95
          : 0.88 + 0.15 * Math.sin(this.time * 11 + phase) + 0.09 * Math.sin(this.time * 17 + phase);
        const offsetX = part ? Math.sin(phase) * footprint * 0.45 : 0;
        const offsetZ = part ? Math.cos(phase) * footprint * 0.45 : 0;
        if (strength > 0.02) {
          const height = (1.2 + strength * 2) * flicker * (part ? 0.74 : 1);
          this.dummy.position.set(p.x + offsetX, p.y + height * 0.43, p.z + offsetZ);
          this.dummy.scale.set(
            footprint * (0.5 + strength * 0.7),
            height,
            footprint * (0.5 + strength * 0.7),
          );
          this.dummy.rotation.set(
            Math.sin(phase + this.time) * 0.09,
            phase,
            Math.cos(phase + this.time) * 0.08,
          );
          this.dummy.updateMatrix();
          this.flames.setMatrixAt(flames, this.dummy.matrix);
          this.color.setHex(part === 0 ? 0xffbe54 : part === 1 ? 0xff7629 : 0xffe08b);
          this.flames.setColorAt(flames++, this.color);
        }
        const cycle = (((this.time * 0.19 + phase) % 1) + 1) % 1;
        const envelope = Math.sin(Math.PI * cycle);
        const scale =
          (0.5 + cycle * 1.8) * footprint * Math.max(0.18, strength || state.heat * 0.35) * envelope;
        this.dummy.position.set(
          p.x + offsetX + cycle * 1.6,
          p.y + 1.8 + cycle * 8,
          p.z + offsetZ + cycle * 0.8,
        );
        this.dummy.scale.set(scale * 1.2, scale, scale * 1.2);
        this.dummy.rotation.set(phase, cycle + phase, this.time * 0.05);
        this.dummy.updateMatrix();
        this.smoke.setMatrixAt(smoke, this.dummy.matrix);
        this.color.setHex(state.target.category === 'vehicle' ? 0x5d6263 : 0x8e9290);
        this.smoke.setColorAt(smoke++, this.color);
      }
    }
    this.flames.count = flames;
    this.smoke.count = smoke;
    for (const mesh of [this.flames, this.smoke]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  incidents(): FireIncident[] {
    if (this.dirtyPositions.size) this.refreshIndex();
    const incidents: FireIncident[] = [];
    for (const id of this.warm) {
      const state = this.states.get(id)!;
      if (
        state.remaining <= 0.005 ||
        (state.intensity < 0.04 && state.heat < IGNITION[state.target.category] * 0.9)
      )
        continue;
      const p = this.moving.has(id) ? state.target.position() : state.point;
      incidents.push({ id, point: { x: p.x, y: p.y, z: p.z }, heat: state.heat, intensity: state.intensity });
    }
    return incidents;
  }

  get active(): number {
    let active = 0;
    for (const id of this.warm) if ((this.states.get(id)?.intensity ?? 0) > 0.04) active++;
    return active;
  }

  get stats(): Record<string, number> {
    return {
      thermalTargets: this.states.size,
      thermalMovingTargets: this.moving.size,
      thermalDirtyTargets: this.dirtyPositions.size,
      thermalPositionRefreshes: this.positionRefreshes,
      thermalFullRefreshes: this.fullRefreshes,
      warmTargets: this.warm.size,
      fires: this.active,
      fireVisuals: this.visualStates.length,
      ignitions: this.ignitions,
      fuelConsumed: this.totalConsumed,
      fireTransfers: this.transfers,
      secondaryExplosions: this.secondaryExplosions,
      queuedSecondary: this.secondaryQueue.length,
      thermalEventCache: this.seen.size,
    };
  }

  /** Clears registrations as well as state. Re-register the restored world after reset. */
  clear(): void {
    this.states.clear();
    this.warm.clear();
    this.grid.clear();
    this.moving.clear();
    this.dirtyPositions.clear();
    this.positionRefreshes = this.fullRefreshes = 0;
    this.seen.clear();
    this.visualStates.length = 0;
    this.secondaryQueue.length = 0;
    this.flames.count = 0;
    this.smoke.count = 0;
    this.time =
      this.accumulator =
      this.indexAge =
      this.ignitions =
      this.secondaryExplosions =
      this.transfers =
      this.totalConsumed =
        0;
    this.visualAge = 1;
  }

  dispose(): void {
    this.clear();
    for (const mesh of [this.flames, this.smoke]) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
  }
}
